"use client"

/**
 * My Listings → Drafts tab body: list of ListingDraftCard rows, client-side
 * title search, empty/loading/error states (mirroring the collections tab's
 * blocks), and the delete confirmation flow. Data comes from the dashboard's
 * useListingDrafts instance (the chip count needs it up there).
 */

import { SquarePen } from "lucide-react"
import { useTranslations } from "next-intl"
import { useMemo, useState } from "react"

import { DeleteDraftDialog } from "@/components/my-listings/delete-draft-dialog"
import { ListingDraftCard } from "@/components/my-listings/listing-draft-card"
import { Skeleton } from "@/components/ui/skeleton"
import { useToast } from "@/hooks/use-toast"
import type { ListingDraftsState } from "@/hooks/use-listing-drafts"

type DeleteFlowState =
  | { kind: "closed" }
  | { kind: "confirming"; draftId: string }
  | { kind: "deleting"; draftId: string }

interface ListingDraftsSectionProps {
  state: ListingDraftsState
  searchQuery: string
  onResumeAction: (draftId: string) => void
  /** Deletes row + files (delete route) and prunes the list state. */
  onDeleteAction: (draftId: string) => Promise<void>
}

export function ListingDraftsSection({
  state,
  searchQuery,
  onResumeAction,
  onDeleteAction,
}: ListingDraftsSectionProps) {
  const t = useTranslations("myListings")
  const { toast } = useToast()
  const [deleteFlow, setDeleteFlow] = useState<DeleteFlowState>({ kind: "closed" })

  const filteredDrafts = useMemo(() => {
    if (state.kind !== "ready") return []
    const query = searchQuery.trim().toLowerCase()
    if (!query) return state.drafts
    return state.drafts.filter((d) => d.title.toLowerCase().includes(query))
  }, [state, searchQuery])

  const handleConfirmDelete = async () => {
    if (deleteFlow.kind !== "confirming") return
    const { draftId } = deleteFlow
    setDeleteFlow({ kind: "deleting", draftId })
    try {
      await onDeleteAction(draftId)
      toast({ title: t("draftDeletedToast") })
    } catch (err) {
      console.error("[drafts-section] delete failed", err)
      toast({ title: t("draftDeleteError"), variant: "destructive" })
    } finally {
      setDeleteFlow({ kind: "closed" })
    }
  }

  if (state.kind === "loading") {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={`draft-skeleton-${i}`} className="flex gap-3 p-2 rounded-2xl">
            <Skeleton className="w-[72px] h-[72px] shrink-0 rounded-xl" />
            <div className="flex-1 min-w-0 space-y-1.5 py-2">
              <Skeleton className="h-3.5 w-2/3 rounded" />
              <Skeleton className="h-3 w-2/5 rounded" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (state.kind === "error") {
    return (
      <div className="text-center p-4">
        <p className="text-sm text-muted-foreground">{t("draftsLoadError")}</p>
      </div>
    )
  }

  if (state.drafts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-3">
          <SquarePen className="w-6 h-6 text-muted-foreground" />
        </div>
        <h3 className="font-semibold mb-1">{t("draftsEmptyTitle")}</h3>
        <p className="text-[14px] text-muted-foreground max-w-[240px]">
          {t("draftsEmptySub")}
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col gap-1">
        {filteredDrafts.map((item) => (
          <ListingDraftCard
            key={item.id}
            item={item}
            isDeleting={deleteFlow.kind === "deleting" && deleteFlow.draftId === item.id}
            onResumeAction={() => onResumeAction(item.id)}
            onDeleteAction={() => setDeleteFlow({ kind: "confirming", draftId: item.id })}
          />
        ))}
        {filteredDrafts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-[14px] text-muted-foreground">{t("noDraftMatch")}</p>
          </div>
        ) : null}
      </div>

      <DeleteDraftDialog
        open={deleteFlow.kind !== "closed"}
        onOpenChange={(open) => {
          if (!open) setDeleteFlow({ kind: "closed" })
        }}
        isDeleting={deleteFlow.kind === "deleting"}
        onConfirmAction={() => {
          void handleConfirmDelete()
        }}
      />
    </>
  )
}
